import PropTypes from "prop-types";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import {
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import DetailHeaderBar from "components/DetailHeaderBar";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import useAuthorized from "hooks/useAuthorized";
import { useCampaign } from "hooks/useCampaign";
import usePageTitle from "hooks/usePageTitle";
import CampaignForm from "routes/campaign/form/CampaignForm";
import {
  BREADCRUMB_LABELS,
  MODEL_TYPE,
  PAGE_TITLES,
} from "routes/campaign/form/constants";
import { FORM_MODES } from "routes/campaign/form/defaults";
import { campaignPaths } from "routes/campaign/paths";

// The same permissions gate the add and edit actions on the Campaigns table.
const CREATE_ACLS = [
  ACL_RELATIONSHIPS.campaign.create,
  ACL_RELATIONSHIPS.campaignStatusPreset.read,
];
const EDIT_ACLS = [ACL_RELATIONSHIPS.campaign.edit];

const CenteredMessage = ({ children }) => (
  <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-3 tw:p-8 tw:text-center tw:text-(--color-text-muted)">
    {children}
  </div>
);

CenteredMessage.propTypes = { children: PropTypes.node };

const BackToList = () => <Link to={campaignPaths.list}>Back to Campaigns</Link>;

/**
 * The page chrome: the same header bar as the record page above a scrolling
 * body. The breadcrumb links back to the list and, on edit, to the campaign.
 */
const PageShell = ({ mode, campaignId, campaignName, children }) => (
  <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:font-sans tw:text-(--color-text-primary)">
    <DetailHeaderBar
      breadcrumb={
        <DetailBreadcrumb
          parentTo={campaignPaths.list}
          parentLabel="All Campaigns"
          trail={
            campaignId
              ? [
                  {
                    to: campaignPaths.detail(campaignId),
                    label: campaignName ?? `Campaign (${campaignId})`,
                  },
                ]
              : []
          }
          current={BREADCRUMB_LABELS[mode]}
        />
      }
      title={PAGE_TITLES[mode]}
    />
    {/* The gutter stays reserved so the form does not shift sideways as the
        scrollbar comes and goes. */}
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        {children}
      </div>
    </div>
  </div>
);

PageShell.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  campaignId: PropTypes.string,
  campaignName: PropTypes.string,
  children: PropTypes.node,
};

/** The full-page Campaign create and edit form. */
const CampaignFormPage = () => {
  // Only the edit route has an :id segment.
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const mode = id ? FORM_MODES.edit : FORM_MODES.create;
  const isEdit = mode === FORM_MODES.edit;
  const returnTo = location.state?.from ?? campaignPaths.list;

  const canWrite = useAuthorized(isEdit ? EDIT_ACLS : CREATE_ACLS);

  const {
    data: record,
    isSuccess: isRecordLoaded,
    isFetchedAfterMount,
    isError: isRecordError,
    error: recordError,
  } = useCampaign(id);
  // isFetchedAfterMount, not just isSuccess: the Campaign page seeds this key
  // with placeholder initialData, and the form latches its defaults at the
  // first render, so mounting on a cached entry could save stale values.
  const isRecordReady = !id || (isRecordLoaded && isFetchedAfterMount);

  const configsQuery = useCustomFieldConfigs(MODEL_TYPE);
  const groupsQuery = useCustomFieldGroups(MODEL_TYPE);

  usePageTitle(PAGE_TITLES[mode]);

  const handleSaved = saved => navigate(campaignPaths.detail(saved.id));
  const handleCancel = () => navigate(returnTo);

  if (!canWrite) {
    return <Navigate to={campaignPaths.list} replace />;
  }

  const shell = content => (
    <PageShell mode={mode} campaignId={id} campaignName={record?.name}>
      {content}
    </PageShell>
  );

  if (isRecordError) {
    const isNotFound = recordError?.response?.status === 404;
    return shell(
      <CenteredMessage>
        <p>
          {isNotFound
            ? "This Campaign does not exist."
            : "Could not load this Campaign. Please try again."}
        </p>
        <BackToList />
      </CenteredMessage>
    );
  }

  if (configsQuery.isError || groupsQuery.isError) {
    return shell(
      <CenteredMessage>
        <p>Could not load the form. Please try again.</p>
        <BackToList />
      </CenteredMessage>
    );
  }

  const isLoading =
    !isRecordReady || configsQuery.isPending || groupsQuery.isPending;

  if (isLoading) {
    return shell(
      <CenteredMessage>
        <Spinner />
      </CenteredMessage>
    );
  }

  return shell(
    <CampaignForm
      mode={mode}
      record={record}
      customFieldConfigs={configsQuery.data ?? []}
      customFieldGroups={groupsQuery.data ?? []}
      onSaved={handleSaved}
      onCancel={handleCancel}
    />
  );
};

export default CampaignFormPage;
